import { HttpErrorResponse } from '@angular/common/http';
import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
	inject,
	signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AlertComponent, CodeEditorComponent, ModalComponent } from '@tecalisdev/infinity';
import {
	IFinishOperation,
	IFormOperation,
	IHomeOperation,
	IOcrOperation,
	IOperation,
	IPolOperation,
	OPERATION_TYPE,
	POL_TEST,
	POL_TYPE,
} from '../../../operations/models/operations.models';
import { hasRetentionOverride } from '../../../shared/utils/retention-duration.util';
import { IWorkflowDetailResponse, IWorkflowsCreatePayload } from '../../models/workflows.models';
import { WorkflowsService } from '../../services/workflows.service';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import { WorkflowFormDefaultsService } from '../../services/workflow-form-defaults.service';
import { WORKFLOW_FORM_I18N_KEYS } from '../../constants/workflow-form-defaults.constants';
import { resolveWorkflowMutationErrorMessage } from '../../utils/workflow-api-error.util';
import { validateWorkflowJsonPayload } from '../../utils/workflow-json-editor-validation.util';

@Component({
	selector: 'app-modal-workflow-json-editor',
	imports: [FormsModule, TranslatePipe, ModalComponent, AlertComponent, CodeEditorComponent],
	templateUrl: './modal-workflow-json-editor.component.html',
	styleUrl: './modal-workflow-json-editor.component.scss',
})
export class ModalWorkflowJsonEditorComponent implements OnInit {
	@Input() json?: IWorkflowDetailResponse;
	@Input() canEdit: boolean = true;
	@Output() closeEvent = new EventEmitter<boolean>();
	@ViewChild('errorAlert') errorAlert?: ElementRef<HTMLElement>;

	//#region Services
	private workflowsService = inject(WorkflowsService);
	private trans = inject(TranslateService);
	private snackbarService = inject(SnackbarService);
	private workflowFormDefaults = inject(WorkflowFormDefaultsService);
	private cdr = inject(ChangeDetectorRef);
	//#endregion

	public showError: string = '';
	public editorValue = '';
	public readonly isSaving = signal(false);

	/** Label del primario: «Guardar» o «Guardando…» (tec-modal no expone loading). */
	public get primaryButtonLabel(): string {
		if (this.isSaving()) {
			return this.trans.instant('BUTTON.SAVING');
		}
		return this.trans.instant('BUTTON.SAVE');
	}

	ngOnInit(): void {
		this.editorValue = this.getJson();
	}

	public closeModal(action: string | null): void {
		if (action === 'buttonPrimary') {
			this.createOrUpdate();
			return;
		}
		if (this.isSaving()) {
			return;
		}
		this.closeEvent.emit();
	}

	private createOrUpdate(): void {
		if (this.isSaving() || !this.canEdit) {
			return;
		}

		this.showError = '';

		let parsedJson;
		try {
			parsedJson = JSON.parse(this.editorValue);
		} catch {
			this.showError = this.trans.instant('WORKFLOWS.CREATE_JSON.INVALID_JSON');
			this.scrollToError();
			return;
		}

		const validationErrors = validateWorkflowJsonPayload(parsedJson, (key, params) =>
			this.trans.instant(key, params),
		);
		if (validationErrors.length > 0) {
			this.showError = validationErrors.join('</br>');
			this.scrollToError();
			return;
		}

		this.isSaving.set(true);

		let submitWorkflow = this.workflowsService.create(parsedJson);
		if (this.json && this.json.uuid) submitWorkflow = this.workflowsService.update(this.json.uuid, parsedJson);

		submitWorkflow.subscribe({
			next: () => {
				this.closeEvent.emit(true);
			},
			error: (error: HttpErrorResponse) => {
				this.isSaving.set(false);
				const fallbackKey = `WORKFLOWS.FORM.${this.json && this.json.uuid ? 'UPDATE_ERROR' : 'CREATE_ERROR'}`;
				this.showError = resolveWorkflowMutationErrorMessage(
					error,
					this.trans.instant(fallbackKey),
					this.trans.instant('WORKFLOWS.FORM.ERROR_DESIGN_COMPANY'),
				);
				this.scrollToError();
			},
			complete: () => {
				this.snackbarService.show(
					this.trans.instant(`WORKFLOWS.FORM.${this.json && this.json.uuid ? 'UPDATE_OK' : 'CREATE_OK'}`),
				);
				this.isSaving.set(false);
			},
		});
	}

	private scrollToError(): void {
		this.cdr.detectChanges();
		const alertEl = this.errorAlert?.nativeElement;
		if (!alertEl) {
			return;
		}

		let parent: HTMLElement | null = alertEl.parentElement;
		while (parent && parent !== document.body) {
			const overflowY = getComputedStyle(parent).overflowY;
			if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
				parent.scrollTop = 0;
			}
			parent = parent.parentElement;
		}

		alertEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}

	// #region JSON Generation
	private getJson(): string {
		if (!this.json) return this.generateBaseJson();
		return this.transformDetailToJson();
	}

	private transformDetailToJson(): string {
		const payload: IWorkflowsCreatePayload = {
			alias: this.json.alias,
			api_key: this.json.api_key,
			design_config_uuid: this.json.design.uuid,
			config: {
				allowed_device: this.json.config.allowed_device,
				version: this.json.config.version,
				location: this.json.config.location,
				include_images: this.json.config.include_images,
				token_duration_minutes: this.json.config.token_duration_minutes ?? null,
				...(hasRetentionOverride(this.json.config)
					? {
							document_retention_days: this.json.config.document_retention_days ?? null,
							media_retention_days: this.json.config.media_retention_days ?? null,
							info_retention_days: this.json.config.info_retention_days ?? null,
						}
					: {}),
				required_recording: this.json.config.required_recording,
				has_saved_state: this.json.config.has_saved_state,
				delayed_process_warning: this.json.config.delayed_process_warning,
				delayed_process_warning_visibility: this.json.config.delayed_process_warning_visibility,
				delayed_process_warning_text: this.json.config.delayed_process_warning_text,
				notify: this.json.config.notify,
				antifraud: this.json.config.antifraud,
				validation: this.json.config.validation,
				sdk: this.json.config.sdk,
				metadata: this.json.config.metadata,
			},
			operations: this.json.operations.map((op: IOperation) => {
				// Crear una copia limpia de la operación sin campos de metadata innecesarios
				const { uuid, completed, ...cleanOperation } = op as any;
				return {
					...cleanOperation,
					...(uuid && { uuid }), // Mantener uuid si existe para edición
				};
			}),
		};

		return JSON.stringify(payload, null, 2);
	}

	private generateBaseJson(): string {
		const baseWorkflow = {
			alias: 'Alias Workflow',
			api_key: null, // TODO: Igual está bien pillar la primera API Key de la compañía
			design_config_uuid: null, // TODO: Igual está bien pillar el primer diseño por defecto de la compañía
			config: this.getBaseConfig(),
			operations: this.getBaseOperations(),
		};
		return JSON.stringify(baseWorkflow, null, 2);
	}

	private getBaseConfig(): IWorkflowsCreatePayload['config'] {
		return {
			allowed_device: ['mobile', 'pc'],
			version: 3,
			location: 'enabled',
			include_images: true,
			required_recording: false,
			has_saved_state: true,
			delayed_process_warning: true,
			delayed_process_warning_text: null,
			notify: null,
			antifraud: {
				ip_block: true,
				identifier_block: true,
				face_block: true,
				block_operation: true,
				email_alert: '',
			},
			validation: {
				type: { type: 'auto', errors: [] },
				emails: null,
			},
			sdk: false,
			metadata: null,
		};
	}

	private getBaseOperations(): IOperation[] {
		return [
			this.getBaseHomeOperation(),
			this.getBaseFormOperation(),
			this.getBaseOcrOperation(),
			this.getBasePolOperation(),
			this.getBaseFinishOperation(),
		];
	}

	private getBaseFormOperation(): IFormOperation {
		const stepTitle = this.workflowFormDefaults.getLocaleMapForKey(
			'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_NAME_QUESTION',
		);

		return {
			type: OPERATION_TYPE.FORM,
			config: {
				tutorial: true,
				steps: [
					{
						uuid: crypto.randomUUID(),
						order: 1,
						title: stepTitle,
						fields: [
							{
								uuid: crypto.randomUUID(),
								id: 'surname',
								name: 'surname',
								type: 'text',
								title: this.workflowFormDefaults.getLocaleMapForKey(
									'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_SURNAME_FIELD',
								),
								col: 12,
								validators: ['required'],
							},
							{
								uuid: crypto.randomUUID(),
								id: 'name',
								name: 'name',
								type: 'text',
								title: this.workflowFormDefaults.getLocaleMapForKey(
									'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_NAME_FIELD',
								),
								col: 12,
								validators: ['required'],
							},
						],
					},
				],
			},
		};
	}

	private getBaseHomeOperation(): IHomeOperation {
		return {
			type: OPERATION_TYPE.HOME,
			config: {
				tecalis_policy_check: true,
				description: { ...this.workflowFormDefaults.getHomeFormDescriptionDefaults() } as any,
				policy_checks: null,
			},
		};
	}

	private getBaseOcrOperation(): IOcrOperation {
		return {
			type: OPERATION_TYPE.OCR,
			config: {
				mode: 'auto',
				tutorial: true,
				ocr_allowed_expiration: 7,
				validate_address: false,
				validate_team_number: false,
				fraud_scoring: {
					aeat: true,
					legal_age: 20,
					expirated: true,
					valid_country: true,
					aml: false,
					pep: false,
					manipulated_photo: {
						blocking_step: true,
						ip_block: true,
						identifier_block: true,
						email_alert: null,
					},
					photocopy_check: {
						blocking_step: true,
						ip_block: false,
						identifier_block: false,
						email_alert: null,
					},
					screen_photo: {
						blocking_step: true,
						ip_block: false,
						identifier_block: false,
						email_alert: null,
					},
				},
				match_data: null,
				setting: {
					total_tries: 9,
					side_max_time: 150,
					show_guide: true,
					total_request: 6,
				},
				allowed_docs: {
					passport: [],
					dni: [],
					nie: true,
				},
				not_allowed_docs: {},
				required_check_nie: false,
				scan_barcode: false,
				fallback: {
					emails: null,
					type: 'manual',
					validation: 'auto',
				},
			},
		};
	}

	private getBasePolOperation(): IPolOperation {
		return {
			type: OPERATION_TYPE.POL,
			config: {
				mode: 'auto',
				tutorial: true,
				setting: {
					total_tries: 2,
					type: POL_TYPE.RANDOM,
					total_test: 2,
					scoring: 6.8,
					available_tests: [
						POL_TEST.SMILE,
						POL_TEST.TURN_RIGHT_HEAD,
						POL_TEST.TURN_LEFT_HEAD,
					],
					max_time: 120,
				},
				max_time: 120,
				fraud_scoring: {
					facial_recognition: true,
					face_match: true,
					percentage_similarity: 60,
					anti_spoofing: true,
				},
				fallback: {
					emails: null,
					type: 'manual',
					validation: 'auto',
				},
			},
		};
	}

	private getBaseFinishOperation(): IFinishOperation {
		return {
			type: OPERATION_TYPE.FINISH,
			config: {
				loading: true,
				loading_text: { ...this.workflowFormDefaults.getFinishLoadingTextDefaults() },
				ok_title: { ...this.workflowFormDefaults.getLocaleMapForKey(WORKFLOW_FORM_I18N_KEYS.FINISH_OK_TITLE) },
				ok_description: {
					...this.workflowFormDefaults.getLocaleMapForKey(WORKFLOW_FORM_I18N_KEYS.FINISH_OK_DESCRIPTION),
				},
				ko_title: { ...this.workflowFormDefaults.getLocaleMapForKey(WORKFLOW_FORM_I18N_KEYS.FINISH_KO_TITLE) },
				ko_description: {
					...this.workflowFormDefaults.getLocaleMapForKey(WORKFLOW_FORM_I18N_KEYS.FINISH_KO_DESCRIPTION),
				},
				cancelled_title: {
					...this.workflowFormDefaults.getLocaleMapForKey(WORKFLOW_FORM_I18N_KEYS.FINISH_CANCELLED_TITLE),
				},
				cancelled_description: {
					...this.workflowFormDefaults.getLocaleMapForKey(WORKFLOW_FORM_I18N_KEYS.FINISH_CANCELLED_DESCRIPTION),
				},
				pending_title: {
					...this.workflowFormDefaults.getLocaleMapForKey(WORKFLOW_FORM_I18N_KEYS.FINISH_PENDING_TITLE),
				},
				pending_description: {
					...this.workflowFormDefaults.getLocaleMapForKey(WORKFLOW_FORM_I18N_KEYS.FINISH_PENDING_DESCRIPTION),
				},
				redirect: null,
				redirect_ko: null,
				redirect_cancelled: null,
				callback: null,
				callback_ko: null,
				callback_cancelled: null,
			},
		};
	}
	// #endregion
}
